import { useState, useEffect, useCallback } from 'react';

// React hook that binds a column-prefs helper (from makeColumnPrefs) to
// component state, auto-persisting on every change and exposing the
// toggle/move/reset actions the dialog needs.
export function useTableColumns(helpers) {
  const [config, setConfig] = useState(helpers.load);
  useEffect(() => { helpers.save(config); }, [config, helpers]);

  const toggle = useCallback((key) => setConfig((cfg) => ({
    ...cfg,
    columns: cfg.columns.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)),
  })), []);

  const move = useCallback((key, delta) => setConfig((cfg) => {
    const arr = [...cfg.columns];
    const i = arr.findIndex((c) => c.key === key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= arr.length) return cfg;
    if (helpers.FIXED_KEYS.has(arr[i].key) || helpers.FIXED_KEYS.has(arr[j].key)) return cfg;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...cfg, columns: arr };
  }), [helpers]);

  const reset = useCallback(() => setConfig(helpers.defaultConfig()), [helpers]);

  // Convenience: given a map of {key: colDef}, return visible column defs
  // in the user's chosen order. Unknown keys drop out.
  const applyTo = useCallback((colMap) => config.columns
    .filter((c) => c.visible)
    .map((c) => colMap[c.key])
    .filter(Boolean), [config]);

  return { config, toggle, move, reset, applyTo };
}
